// src/library/chat/conversation-listed.tsx
import { useNavigate } from "react-router-dom";
import { Button, View, Text, Flex } from "@aws-amplify/ui-react";
import { Agent, Conversation } from "@heartcheck/shared";
import { useState } from "react";
import { useAgentApiDeleteConversation } from "@heartcheck/shared-react";
import { useSetRecoilState } from "recoil";
import {
  Conversations,
  ConversationEvents,
  ConversationPartialResults,
  Loadable,
} from "@heartcheck/shared-react";

function timeSince(dateString: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);

  const intervals = [
    { label: "year", seconds: 31536000 },
    { label: "month", seconds: 2592000 },
    { label: "day", seconds: 86400 },
    { label: "hour", seconds: 3600 },
    { label: "minute", seconds: 60 },
    { label: "second", seconds: 1 },
  ];

  for (const { label, seconds: interval } of intervals) {
    const count = Math.floor(seconds / interval);
    if (count >= 1) return `${count} ${label}${count > 1 ? "s" : ""} ago`;
  }

  return "just now";
}

interface Props {
  conversation: Conversation;
  agent?: Agent;
}

export function AgentApiConversationListed({ conversation, agent }: Props) {
  const navigate = useNavigate();
  const deleteConversation = useAgentApiDeleteConversation();

  // Local UI state
  const [hovering, setHovering] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Recoil setters (no reads → no unused var warning)
  const setConversationsState = useSetRecoilState(Conversations);
  const setEventsState = useSetRecoilState(ConversationEvents);
  const setMetaState = useSetRecoilState(ConversationPartialResults);

  const handleClick = () => {
    if (!deleting) {
      navigate(`/chat/view/${conversation.id}`);
    }
  };

  const handleDelete = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (deleting) return;

    try {
      setDeleting(true);
      await deleteConversation(conversation.id);

      // Optimistic eviction from client stores
      setConversationsState((prev) => {
        if (!prev?.value) return prev;
        const next = prev.value.without(conversation.id);
        return Loadable.loaded(next);
      });

      setEventsState((prev) => {
        const { [conversation.id]: _drop, ...rest } = prev;
        return rest;
      });

      setMetaState((prev) => {
        const { [conversation.id]: _drop, ...rest } = prev;
        return rest;
      });

      navigate("/chat/new");
    } catch (err) {
      console.error("❌ Failed to delete conversation:", err);
      alert("Sorry, something went wrong deleting this chat. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <View
      position="relative"
      paddingBlock={6}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      opacity={deleting ? 0.6 : 1}
    >
      <Button onClick={handleClick} isFullWidth variation="link" isDisabled={deleting}>
        <Flex direction="column" textAlign="left" width="100%">
          <Text fontWeight="bold">{agent?.name || "[deleted agent]"}</Text>
          <Text fontSize="small" color="font.secondary">
            {timeSince(conversation.timestamp)}
          </Text>
        </Flex>
      </Button>

      {hovering && (
        <Button
          variation="warning"
          size="small"
          position="absolute"
          right={10}
          top={10}
          onClick={handleDelete}
          aria-label="Delete conversation"
          isDisabled={deleting}
        >
          <i className="fa-solid fa-trash" />
        </Button>
      )}
    </View>
  );
}
